import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { IPickerListItem, Icon, Picker } from "@livechat/design-system-react-components";

import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { PickerContainerWithLabel } from "routes/automations/shared/components/PickerContainerWithLabel";
import {
  IAgent,
  IWithAgentsState,
  selectAgents,
  selectAgentsAlphabetically,
  selectLoggedInAgent,
} from "store/entities/agents";
import { IRuleTriggerAgentChanged } from "store/entities/rules/interfaces";
import { IWithSessionState } from "store/features/session";

import { TRIGGER_ANY_AGENT_OPTION_KEY, TRIGGER_UNASSIGNED_OPTION_KEY, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerAgentChanged>;
}

export const AgentChangedTrigger: FC<IProps> = ({ input }) => {
  const agentsById = useSelector(selectAgents);
  const agents = useSelector((state: IWithAgentsState & IWithSessionState) =>
    selectAgentsAlphabetically(state, {
      loggedInAgentOnTop: true,
      activeOnly: true,
      excludeViewers: true,
    })
  );
  const loggedInAgent = useSelector(selectLoggedInAgent);

  const { oldAgentIn, newAgentIn } = input.value;

  const agentOptions = [
    UNASSIGNED_OPTION,
    ANY_AGENT_OPTION,
    ...agents.map(agent => createAgentOption(agent, loggedInAgent)),
  ];

  const selectedFromAgentOptions = oldAgentIn
    ? oldAgentIn.map(id => {
        if (id === TRIGGER_UNASSIGNED_OPTION_KEY) {
          return UNASSIGNED_OPTION;
        }

        if (id === TRIGGER_ANY_AGENT_OPTION_KEY) {
          return ANY_AGENT_OPTION;
        }

        const agent = agentsById[id];
        return agent ? createAgentOption(agent, loggedInAgent) : createRemovedAgentOption(id);
      })
    : [UNASSIGNED_OPTION, ANY_AGENT_OPTION];

  const selectedToAgentOptions = newAgentIn
    ? newAgentIn.map(id => {
        if (id === TRIGGER_UNASSIGNED_OPTION_KEY) {
          return UNASSIGNED_OPTION;
        }

        if (id === TRIGGER_ANY_AGENT_OPTION_KEY) {
          return ANY_AGENT_OPTION;
        }

        const agent = agentsById[id];
        return agent ? createAgentOption(agent, loggedInAgent) : createRemovedAgentOption(id);
      })
    : [ANY_AGENT_OPTION];

  const handleSelectAgent =
    (property: "oldAgentIn" | "newAgentIn", previousSelected: IPickerListItem[]) =>
    (selectedOptions: IPickerListItem[] | null): void => {
      if (!selectedOptions) {
        changeTriggerValues(input, { [property]: [] });
        return;
      }

      const currentOptionKeys = selectedOptions.map(opt => opt.key);
      const previousOptionKeys = previousSelected.map(opt => opt.key);

      const isAnyAgentCurrentlySelected = currentOptionKeys.includes(TRIGGER_ANY_AGENT_OPTION_KEY);
      const isAnyAgentPreviouslySelected = previousOptionKeys.includes(TRIGGER_ANY_AGENT_OPTION_KEY);
      const isUnassignedCurrentlySelected = currentOptionKeys.includes(TRIGGER_UNASSIGNED_OPTION_KEY);
      const isUnassignedPreviouslySelected = previousOptionKeys.includes(TRIGGER_UNASSIGNED_OPTION_KEY);

      let agentIn: string[] | undefined;

      // All those cases are explained in confluence
      // https://livechatinc.atlassian.net/wiki/x/BQAoy
      if (isAnyAgentCurrentlySelected && isAnyAgentPreviouslySelected) {
        if (isUnassignedCurrentlySelected && isUnassignedPreviouslySelected) {
          agentIn = currentOptionKeys.filter(key => key !== TRIGGER_ANY_AGENT_OPTION_KEY);
        } else if (isUnassignedCurrentlySelected) {
          // for "oldAgentIn" value undefined means in the BE (unassigned + any agent), for "newAgentIn" just any agent
          agentIn = property === "oldAgentIn" ? undefined : currentOptionKeys;
        } else if (isUnassignedPreviouslySelected) {
          agentIn =
            property === "oldAgentIn"
              ? currentOptionKeys.filter(key => key !== TRIGGER_UNASSIGNED_OPTION_KEY)
              : undefined;
        } else {
          agentIn = currentOptionKeys.filter(key => key !== TRIGGER_ANY_AGENT_OPTION_KEY);
        }
      } else if (isAnyAgentCurrentlySelected) {
        if (isUnassignedCurrentlySelected) {
          agentIn =
            property === "oldAgentIn"
              ? undefined
              : currentOptionKeys.filter(
                  key => key === TRIGGER_ANY_AGENT_OPTION_KEY || key === TRIGGER_UNASSIGNED_OPTION_KEY
                );
        } else {
          agentIn =
            property === "oldAgentIn"
              ? currentOptionKeys.filter(
                  key => key === TRIGGER_ANY_AGENT_OPTION_KEY || key === TRIGGER_UNASSIGNED_OPTION_KEY
                )
              : undefined;
        }
      } else if (isAnyAgentPreviouslySelected) {
        agentIn = currentOptionKeys.filter(key => key !== TRIGGER_ANY_AGENT_OPTION_KEY);
      } else {
        agentIn = currentOptionKeys;
      }

      changeTriggerValues(input, { [property]: agentIn });
    };

  const handleSelectFromAgent = handleSelectAgent("oldAgentIn", selectedFromAgentOptions);

  const handleSelectToAgent = handleSelectAgent("newAgentIn", selectedToAgentOptions);

  return (
    <>
      <PickerContainerWithLabel label="from">
        <Picker
          type="multi"
          options={agentOptions}
          selected={selectedFromAgentOptions}
          onSelect={handleSelectFromAgent}
          placeholder="Select one agent or more…"
        />
      </PickerContainerWithLabel>
      <PickerContainerWithLabel label="to">
        <Picker
          type="multi"
          options={agentOptions}
          selected={selectedToAgentOptions}
          onSelect={handleSelectToAgent}
          placeholder="Select one agent or more…"
        />
      </PickerContainerWithLabel>
    </>
  );
};

const createAgentOption = (agent: IAgent, loggedInAgent: IAgent): IPickerListItem => ({
  key: agent.id,
  name: agent.name,
  customElement: {
    selectedItemBody: <AgentPickerSelectedListItem agentName={agent.name} avatarUrl={agent.avatar} />,
    listItemBody: (
      <AgentPickerListItem
        agentName={agent.name}
        avatarUrl={agent.avatar}
        isLoggedInAgent={agent.id === loggedInAgent.id}
      />
    ),
  },
});

const UNASSIGNED_OPTION = {
  key: TRIGGER_UNASSIGNED_OPTION_KEY,
  name: "Unassigned",
};

const ANY_AGENT_OPTION = {
  key: TRIGGER_ANY_AGENT_OPTION_KEY,
  name: "Any agent",
};

const createRemovedAgentOption = (agentId: string): IPickerListItem => ({
  key: agentId,
  name: "Agent removed",
  selectedTagOptions: {
    kind: "error",
    leftNode: <Icon source={Error} />,
  },
});
